import { useState } from 'react';
import { moderateContent } from '../../api/moderationApi';
import { getApiAxiosInstance } from '../../utils/network';

const useModerateText = ({ validateFn, asyncValidateFn, disableProfanityCheck }) => {
    const [asyncCache, setAsyncCache] = useState({});

    const validateSync = (text) => {
        const validationError = validateFn ? validateFn(text) : null;
        if (validationError) {
            return validationError;
        }

        if (text in asyncCache) {
            return asyncCache[text];
        }

        return null;
    };

    const moderateAsync = async (text) => {
        if (disableProfanityCheck || !text) {
            return null;
        }

        try {
            const moderationResult = await moderateContent(getApiAxiosInstance(), { text });
            const { data } = moderationResult;

            if (!data.ok) {
                return {
                    error: data.message,
                    noCache: !data.canCache,
                };
            }

            return null;
        } catch {
            // Let the user continue without moderation if there is an error
            return null;
        }
    };

    const validateAsync = async (text, submitting) => {
        // Only make our async validation call when the form is submitting. This prevents hammering
        // the backend with partial text.
        if (!submitting) {
            return null;
        }

        if (text in asyncCache) {
            return asyncCache[text];
        }

        if (asyncValidateFn) {
            const error = await asyncValidateFn(text);
            if (error?.error) {
                setAsyncCache((prevCache) => ({
                    ...prevCache,
                    [text]: error,
                }));

                return error;
            }
        }

        const error = await moderateAsync(text);
        if (error?.error) {
            // The moderation endpoint may return a result that should not be cached.
            if (!error.noCache) {
                setAsyncCache((prevCache) => ({
                    ...prevCache,
                    [text]: error,
                }));
            }

            return error;
        }

        setAsyncCache((prevCache) => ({
            ...prevCache,
            [text]: null,
        }));

        return null;
    };

    return { validateSync, validateAsync };
};

export default useModerateText;
